import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const MetadataContainer = styled.div`
  margin-top: 20px;
  padding: 15px;
  background-color: #f0f0f0;
  border-radius: 8px;
`;

const FormGroup = styled.div`
  margin-bottom: 15px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  height: 80px;
  resize: vertical;
`;

const Button = styled.button`
  padding: 8px 16px;
  background-color: #333;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  
  &:hover {
    background-color: #555;
  }
`;

const MetadataEditor = ({ svgCode, setSvgCode }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [author, setAuthor] = useState('');
  const [keywords, setKeywords] = useState('');

  useEffect(() => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(svgCode, 'image/svg+xml');
      
      // Extract metadata
      const titleElement = doc.querySelector('title');
      const descElement = doc.querySelector('desc');
      const metadataElement = doc.querySelector('metadata');
      
      setTitle(titleElement ? titleElement.textContent : '');
      setDescription(descElement ? descElement.textContent : '');
      
      // Extract RDF metadata if available
      if (metadataElement) {
        const rdfElement = metadataElement.querySelector('rdf\\:RDF, RDF');
        if (rdfElement) {
          const authorElement = rdfElement.querySelector('dc\\:creator, creator');
          const keywordsElement = rdfElement.querySelector('dc\\:subject, subject');
          
          setAuthor(authorElement ? authorElement.textContent : '');
          setKeywords(keywordsElement ? keywordsElement.textContent : '');
        }
      }
    } catch (err) {
      console.error('Error parsing SVG metadata:', err);
    }
  }, [svgCode]);

  const handleApplyMetadata = () => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(svgCode, 'image/svg+xml');
      const svgElement = doc.querySelector('svg');
      
      if (svgElement) {
        // Update or create title
        let titleElement = doc.querySelector('title');
        if (!titleElement && title) {
          titleElement = doc.createElementNS('http://www.w3.org/2000/svg', 'title');
          svgElement.insertBefore(titleElement, svgElement.firstChild);
        }
        if (titleElement) {
          titleElement.textContent = title;
        }
        
        // Update or create description
        let descElement = doc.querySelector('desc');
        if (!descElement && description) {
          descElement = doc.createElementNS('http://www.w3.org/2000/svg', 'desc');
          if (titleElement) {
            svgElement.insertBefore(descElement, titleElement.nextSibling);
          } else {
            svgElement.insertBefore(descElement, svgElement.firstChild);
          }
        }
        if (descElement) {
          descElement.textContent = description;
        }
        
        // Simple metadata without RDF for simplicity
        svgElement.setAttribute('data-author', author);
        svgElement.setAttribute('data-keywords', keywords);
        
        setSvgCode(new XMLSerializer().serializeToString(doc));
      }
    } catch (err) {
      console.error('Error updating SVG metadata:', err);
    }
  };

  return (
    <MetadataContainer>
      <h3>SVG Metadata</h3>
      
      <FormGroup>
        <Label htmlFor="svg-title">Title:</Label>
        <Input 
          id="svg-title"
          type="text" 
          value={title} 
          onChange={(e) => setTitle(e.target.value)}
          placeholder="SVG Title"
        />
      </FormGroup>
      
      <FormGroup>
        <Label htmlFor="svg-desc">Description:</Label>
        <Textarea 
          id="svg-desc"
          value={description} 
          onChange={(e) => setDescription(e.target.value)}
          placeholder="SVG Description"
        />
      </FormGroup>
      
      <FormGroup>
        <Label htmlFor="svg-author">Author:</Label>
        <Input 
          id="svg-author"
          type="text" 
          value={author} 
          onChange={(e) => setAuthor(e.target.value)}
          placeholder="Author Name"
        />
      </FormGroup>
      
      <FormGroup>
        <Label htmlFor="svg-keywords">Keywords:</Label>
        <Input 
          id="svg-keywords"
          type="text" 
          value={keywords} 
          onChange={(e) => setKeywords(e.target.value)}
          placeholder="Comma-separated keywords"
        />
      </FormGroup>
      
      <Button onClick={handleApplyMetadata}>Apply Metadata</Button>
    </MetadataContainer>
  );
};

export default MetadataEditor; 